import './PwaPushBanner.css';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import UserService from '../../Service/UserService';
import TokenService from '../../Service/TokenService';
import PushNotificationService from '../../Service/PushNotificationService';
import { reportError } from '../../utils/reportError';

const DISMISS_KEY = 'pushBannerDismissedAt';

// Standalone PWA detection: `display-mode: standalone` covers installed
// Chromium/desktop PWAs, `navigator.standalone` covers iOS Safari home-screen
// apps (which never report a matching display-mode media query).
function isStandalonePwa() {
    const matchesDisplayMode =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(display-mode: standalone)').matches;
    return matchesDisplayMode || window.navigator.standalone === true;
}

/**
 * Prompts a master to enable push notifications right after opening the
 * installed PWA, instead of leaving that discoverable only via the manual
 * toggle in Settings → Сповіщення → Майстри. Most modern browsers require
 * an explicit user gesture before `Notification.requestPermission()` can be
 * called, so this renders a dismissible banner with a CTA rather than
 * silently requesting permission on mount. The CTA calls the same
 * `PushNotificationService.subscribe` used by that manual toggle — no
 * separate subscription path.
 */
function PwaPushBanner() {
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!UserService.isAuthenticated()) { setVisible(false); return; }
        if (!PushNotificationService.isSupported()) return;
        if (typeof Notification === 'undefined') return;
        if (!isStandalonePwa()) return;
        if (Notification.permission === 'denied') return;
        if (localStorage.getItem(DISMISS_KEY)) return;
        if (PushNotificationService.getSubscribedMasterId() != null) return;
        setVisible(true);
        // Re-check on route change too, so the banner appears once the user
        // lands on a protected page right after logging in (App mounts once
        // per SPA session, before authentication may exist yet).
    }, [location.pathname]);

    const handleEnable = async () => {
        const masterId = TokenService.getUserId();
        if (!masterId) return;
        setLoading(true);
        setError('');
        try {
            await PushNotificationService.subscribe(masterId);
            setVisible(false);
        } catch (err) {
            if (err?.message !== 'permission_denied') reportError(err, { where: 'PwaPushBanner.enable' });
            setError(
                err?.message === 'permission_denied'
                    ? 'Дозвіл заблоковано в браузері.'
                    : 'Не вдалось увімкнути. Спробуйте пізніше в Налаштуваннях → Сповіщення.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // ignore storage errors (e.g. private mode quota)
        }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div className="pwa-push-banner" role="status">
            <Bell size={16} className="pwa-push-banner__icon" />
            <div className="pwa-push-banner__body">
                <span className="pwa-push-banner__text">
                    Увімкніть push-сповіщення, щоб не пропускати нові записи та нагадування.
                </span>
                {error && <span className="pwa-push-banner__error">{error}</span>}
            </div>
            <button className="pwa-push-banner__btn" onClick={handleEnable} disabled={loading}>
                {loading ? '…' : 'Увімкнути сповіщення'}
            </button>
            <button
                className="pwa-push-banner__close"
                onClick={handleDismiss}
                aria-label="Закрити"
                disabled={loading}
            >
                <X size={14} />
            </button>
        </div>
    );
}

export default PwaPushBanner;
